import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { App, Button, Drawer, Form, Input, InputNumber, Select, Space, Switch, Table, Tag, Typography, } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiError } from '@/lib/api';
import { formatRupiah } from '@/lib/format';
import { useAuthStore } from '@/lib/auth-store';
export function ProductsPage() {
    const { message } = App.useApp();
    const qc = useQueryClient();
    const hasPerm = useAuthStore((s) => s.hasPerm);
    const [q, setQ] = useState('');
    const [category, setCategory] = useState();
    const [lowStock, setLowStock] = useState(false);
    const [drawer, setDrawer] = useState({ open: false });
    const [form] = Form.useForm();
    const cats = useQuery({
        queryKey: ['toko-categories-select'],
        queryFn: async () => (await api.get('/toko/categories')).data,
    });
    const products = useQuery({
        queryKey: ['toko-products', q, category, lowStock],
        queryFn: async () => (await api.get('/toko/products', {
            params: { q, category_id: category, low_stock: lowStock || undefined },
        })).data,
    });
    const createMut = useMutation({
        mutationFn: async (v) => (await api.post('/toko/products', v)).data,
        onSuccess: () => {
            message.success('Produk ditambahkan');
            qc.invalidateQueries({ queryKey: ['toko-products'] });
            setDrawer({ open: false });
        },
        onError: (e) => message.error(apiError(e)),
    });
    const patchMut = useMutation({
        mutationFn: async ({ id, body }) => (await api.patch(`/toko/products/${id}`, body)).data,
        onSuccess: () => {
            message.success('Produk diperbarui');
            qc.invalidateQueries({ queryKey: ['toko-products'] });
            setDrawer({ open: false });
        },
        onError: (e) => message.error(apiError(e)),
    });
    const cols = [
        { title: 'SKU', dataIndex: 'sku', width: 130 },
        { title: 'Nama', dataIndex: 'name' },
        { title: 'Kategori', dataIndex: 'category_name', width: 140 },
        { title: 'Unit', dataIndex: 'unit', width: 80 },
        {
            title: 'Harga',
            dataIndex: 'price_sell',
            width: 140,
            align: 'right',
            render: (v) => _jsx("span", { className: "money", children: formatRupiah(v) }),
        },
        {
            title: 'Stok',
            width: 130,
            align: 'right',
            render: (_, r) => {
                const qty = parseFloat(r.stock_qty);
                const rp = parseFloat(r.reorder_point);
                return (_jsxs(Space, { direction: "vertical", size: 0, children: [_jsx("span", { className: "money", children: qty.toFixed(2) }), qty <= rp && _jsx(Tag, { color: "red", children: "Reorder" })] }));
            },
        },
        {
            title: 'Avg Cost',
            dataIndex: 'stock_avg_cost',
            width: 140,
            align: 'right',
            render: (v) => _jsx("span", { className: "money", children: formatRupiah(v) }),
        },
        {
            title: 'Status',
            dataIndex: 'is_active',
            width: 100,
            render: (a) => (a ? _jsx(Tag, { color: "success", children: "Aktif" }) : _jsx(Tag, { children: "Nonaktif" })),
        },
        {
            title: 'Aksi',
            width: 90,
            render: (_, r) => hasPerm('product:edit') && (_jsx(Button, { size: "small", icon: _jsx(EditOutlined, {}), onClick: () => {
                    setDrawer({ open: true, row: r });
                    form.setFieldsValue({
                        ...r,
                        price_sell: parseFloat(r.price_sell),
                        reorder_point: parseFloat(r.reorder_point),
                    });
                } })),
        },
    ];
    return (_jsxs("div", { children: [_jsxs(Space, { style: { marginBottom: 16, display: 'flex', justifyContent: 'space-between', width: '100%' }, children: [_jsx(Typography.Title, { level: 3, style: { margin: 0 }, children: "Produk" }), hasPerm('product:create') && (_jsx(Button, { type: "primary", icon: _jsx(PlusOutlined, {}), onClick: () => { form.resetFields(); setDrawer({ open: true }); }, children: "Tambah Produk" }))] }), _jsxs(Space, { style: { marginBottom: 16 }, wrap: true, children: [_jsx(Input.Search, { placeholder: "Cari SKU / nama\u2026", allowClear: true, style: { width: 260 }, onSearch: (v) => setQ(v) }), _jsx(Select, { allowClear: true, style: { width: 200 }, placeholder: "Kategori", value: category, onChange: (v) => setCategory(v), options: (cats.data ?? []).map((c) => ({ value: c.id, label: c.name })) }), _jsxs(Space, { children: [_jsx(Switch, { checked: lowStock, onChange: setLowStock }), _jsx("span", { children: "Hanya stok rendah" })] })] }), _jsx(Table, { rowKey: "id", loading: products.isLoading, dataSource: products.data ?? [], columns: cols, pagination: { pageSize: 30 }, size: "middle" }), _jsx(Drawer, { open: drawer.open, onClose: () => setDrawer({ open: false }), title: drawer.row ? `Edit ${drawer.row.sku}` : 'Tambah Produk', width: 480, destroyOnClose: true, extra: _jsx(Button, { type: "primary", loading: createMut.isPending || patchMut.isPending, onClick: () => form.validateFields().then((v) => {
                        const payload = {
                            ...v,
                            price_sell: v.price_sell?.toString(),
                            reorder_point: v.reorder_point?.toString(),
                        };
                        if (drawer.row)
                            patchMut.mutate({ id: drawer.row.id, body: payload });
                        else
                            createMut.mutate(payload);
                    }), children: "Simpan" }), children: _jsxs(Form, { form: form, layout: "vertical", preserve: false, children: [_jsx(Form.Item, { name: "sku", label: "SKU", rules: [{ required: true }], children: _jsx(Input, { disabled: !!drawer.row }) }), _jsx(Form.Item, { name: "name", label: "Nama", rules: [{ required: true }], children: _jsx(Input, {}) }), _jsx(Form.Item, { name: "category_id", label: "Kategori", children: _jsx(Select, { allowClear: true, options: (cats.data ?? []).map((c) => ({ value: c.id, label: c.name })) }) }), _jsx(Form.Item, { name: "unit", label: "Satuan (unit)", rules: [{ required: true }], children: _jsx(Input, { placeholder: "pcs / dus / kg" }) }), _jsx(Form.Item, { name: "price_sell", label: "Harga Jual", rules: [{ required: true }], children: _jsx(InputNumber, { style: { width: '100%' }, min: 0, formatter: (v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.'), parser: ((v) => parseFloat((v ?? '').replace(/\./g, '')) || 0) }) }), _jsx(Form.Item, { name: "reorder_point", label: "Reorder Point", children: _jsx(InputNumber, { style: { width: '100%' }, min: 0, step: 0.1 }) }), _jsx(Form.Item, { name: "notes", label: "Catatan", children: _jsx(Input.TextArea, { rows: 2 }) }), drawer.row && (_jsx(Form.Item, { name: "is_active", label: "Aktif", valuePropName: "checked", children: _jsx(Switch, {}) }))] }) })] }));
}
